<template>
  <b-container fluid id="main-container" class="p-0">
    <!-- Header -->
    <header class="instruction-section flex-shrink-0">
      <h1 class="instruction-section-title">
        {{ $store.getters.language === 'SLO' ? model_name_slo : model_name }}
      </h1>
      <p class="instruction-section-text">
        {{ $store.getters.language === 'SLO' ? 'Pogovarjaj se z jezikovnim modelom' : 'Talk to the language model' }}
      </p>
    </header>

    <b-row no-gutters class="app-body">
      <!-- Main Chat Column (Order 1 on Mobile, 2 on Desktop) -->
      <b-col cols="12" lg="8" xl="9" class="order-1 order-lg-2 chat-column">
        
        <!-- Model Switcher -->
        <div class="model-switcher-bar p-2 border-bottom bg-light">
          <b-form-radio-group
            v-model="selectedModel"
            :options="modelOptions"
            buttons
            button-variant="outline-secondary"
            size="sm"
            name="model-selector"
            class="w-100"
          ></b-form-radio-group>
        </div>

        <!-- Chat Scroll Area -->
        <div id="chatArea" ref="chatArea" class="p-3">
          <div v-for="message in messages" :key="message.idx" 
               :class="['message-wrapper', message.role === 'LLM' ? 'llm-align' : 'user-align']">
            
            <div 
              :class="message.role === 'LLM' ? 'llm_message' : 'user_message'"
              :style="message.role === 'Me' && message.image ? 'max-width: 50%' : ''"
            >
              <span class="role-label">{{ message.role }}</span>
              
              <div v-if="message.image" class="message-image-container my-2">
                <img :src="message.image" class="img-fluid rounded shadow-sm" />
              </div>

              <div class="message-text">
                {{ message.role === 'LLM' && message.idx == messages.length - 1 ? last_message : message.text }}
              </div>
            </div>
          </div>
          <div ref="bottomEl"></div>
        </div>

        <!-- Sticky Input Footer -->
        <footer class="input-section p-3 border-top bg-white">
          <div v-if="selectedImagePreview" class="preview-container">
            <img :src="selectedImagePreview" class="img-thumbnail" />
            <div class="remove-preview" @click="clearImage">&times;</div>
          </div>

          <div class="d-flex align-items-end gap-2">
            <input type="file" ref="fileInput" hidden accept="image/*" @change="handleImageUpload" />
            
            <b-button variant="outline-secondary" class="rounded-circle action-btn" @click="$refs.fileInput.click()">
              📎
            </b-button>

            <textarea 
              id="chatInput" 
              v-model="chatInput" 
              class="form-control flex-grow-1 mx-2" 
              rows="1" 
              @keyup.enter.exact.prevent="submitChat"
              placeholder="Type your message..."
            ></textarea>

            <div class="d-flex flex-column gap-1">
              <b-button variant="dark" @click="submitChat" class="px-3 rounded-pill mb-1">Send</b-button>
            </div>
          </div>
          <div v-if="loading" class="text-center mt-2">
            <b-spinner small type="grow" variant="secondary"></b-spinner>
          </div>
        </footer>
      </b-col>

      <!-- Settings Sidebar (Order 2 on Mobile, 1 on Desktop) -->
      <b-col cols="12" lg="4" xl="3" class="order-2 order-lg-1 sidebar-section p-4">
        <div class="sidebar-content">
          <div class="instruction-prompt mb-4">
            <label class="small font-weight-bold">System Instructions</label>
            <select class="form-control mb-2" v-model="system_prompt">
              <option v-for="item in system_prompts" :key="item">{{ item }}</option>
            </select>
            <div class="d-flex">
              <input class="form-control form-control-sm" v-model="custom_prompt" placeholder="Add custom..."/>
              <b-button size="sm" variant="dark" @click="addPrompt" class="ml-1">Add</b-button>
            </div>
          </div>

          <div class="custom-slider">
            <p class="mb-1 small">Max Tokens: <strong>{{num_tokens}}</strong></p>
            <input v-model="num_tokens" type="range" min="0" max="4096" class="w-100 cursor-pointer"/>
          </div>

          <div class="custom-slider">
            <p class="mb-1 small">Temperature: <strong>{{temperature}}</strong></p>
            <input v-model="temperature" type="range" min="0" max="2" step="0.1" class="w-100 cursor-pointer"/>
          </div>
          
          <hr />
          <b-button variant="outline-danger" size="sm" block @click="clearChat">Clear Chat History</b-button>
          
          <div v-if="error" class="error-text small mt-3 font-weight-bold text-danger">{{ error }}</div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
export default {
  name: "LLMChat",
  data() {
    return {
      selectedModel: 'GaMS3-12B-Multimodal',
      modelOptions: [
        { text: 'Gemma-3', value: 'gemma-3-12b-it' },
        { text: 'SVILA', value: 'SVILA-1-12B' },
        { text: 'GaMS3', value: 'GaMS3-12B-Multimodal' }
      ],
      chatInput: '',
      messages: [],
      model_id: '',
      model_name: '',
      model_name_slo: '',
      error: '',
      counter: 0,
      last_message: '',
      last_message_full: '',
      history: [],
      consider_history: true,
      loading: false,
      num_tokens: 1024,
      temperature: 0.8,
      custom_prompt: "",
      system_prompt: 'No system prompt.',
      system_prompts: ['No system prompt.', 'You are a helpful assistant.'],
      selectedImage: null,
      selectedImagePreview: null,
    };
  },
  mounted() {
    if (typeof this.$route.params['model_id'] !== 'undefined') {
      this.model_id = this.$route.params['model_id'];
      this.model_name = this.$route.params['model_name'];
      this.model_name_slo = this.$route.params['model_name_slo'];
      this.$store.dispatch('save_model_id', this.model_id);
      this.$store.dispatch('save_model_name', this.model_name);
      this.$store.dispatch('save_model_name_slo', this.model_name_slo);
    } else {
      this.model_id = this.$store.getters.model_id;
      this.model_name = this.$store.getters.model_name;
      this.model_name_slo = this.$store.getters.model_name_slo;
    }
  },

  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.bottomEl;
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      });
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedImage = file;
        const reader = new FileReader();
        reader.onload = (e) => { this.selectedImagePreview = e.target.result; };
        reader.readAsDataURL(file);
      }
    },
    clearImage() {
      this.selectedImage = null;
      this.selectedImagePreview = null;
    },
    typeWriter() {
      if (this.counter < this.last_message_full.length) {
        this.last_message += this.last_message_full.charAt(this.counter);
        this.counter++;
        setTimeout(this.typeWriter, 10);
        this.scrollToBottom();
      }
    },
    clearChat() {
      this.history = [];
      this.messages = [];
      this.last_message = '';
    },
    addPrompt() {
      if (this.custom_prompt.trim()) {
        this.system_prompts.push(this.custom_prompt);
        this.system_prompt = this.custom_prompt;
        this.custom_prompt = "";
      }
    },
    submitChat() {
      if (!this.chatInput.trim() && !this.selectedImage) return;
      
      this.error = '';
      const payload = {
        "model": this.selectedModel, // Using the switcher value
        "prompt": this.chatInput,
        "image_data": this.selectedImagePreview,
        "temperature": parseFloat(this.temperature),
        "max_tokens": parseInt(this.num_tokens),
        "instruction": this.system_prompt === 'No system prompt.' ? "" : this.system_prompt,
        "history": this.history,
        "consider_history": this.consider_history,
      };

      this.messages.push({
        idx: this.messages.length, 
        role: 'Me', 
        text: this.chatInput, 
        image: this.selectedImagePreview 
      });

      const currentInput = this.chatInput;
      this.chatInput = "";
      this.loading = true;
      this.clearImage();
      this.scrollToBottom();

      this.$http.post(this.$hostname + '/api/model/' + this.model_id + '/get', payload)
        .then(response => {
          this.loading = false;
          this.counter = 0;
          this.last_message = '';
          this.history.push([currentInput, response.data]);
          this.messages.push({ idx: this.messages.length, role: 'LLM', text: response.data });
          this.last_message_full = response.data;
          this.typeWriter();
        })
        .catch(() => { this.loading = false; this.error = "Connection error."; });
    }
  }
}
</script>

<style lang="scss" scoped>

#main-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
$tablet-width: 768px;

@media (max-width: #{$tablet-width}) {
  #main-container {
    min-height: 120vh;
  }
}

.app-body {
  flex: 1;
}

.chat-column {
  display: flex;
  flex-direction: column;
  background: #fff;
  @media (min-width: 992px) {
    height: calc(100vh - 80px); 
  }
}

#chatArea {
  flex: 1;
  overflow-y: auto;
  background: #f8f9fa;
  /* Ensure chat area is large enough on mobile before sidebar appears */
  min-height: 50vh; 
}

.input-section {
  position: sticky;
  bottom: 0;
  z-index: 100;
  background: white;
}

.sidebar-section {
  background: #f1f3f5;
  border-top: 1px solid #dee2e6;
  @media (min-width: 992px) {
    border-top: none;
    height: calc(100vh - 80px);
    overflow-y: auto;
  }
}

/* Messages Bubbles */
.message-wrapper { display: flex; width: 100%; margin-bottom: 1.5rem; }
.user-align { justify-content: flex-end; }
.llm-align { justify-content: flex-start; }

.user_message { 
  background: #4F6675; 
  color: white; 
  border-radius: 18px 18px 2px 18px; 
  padding: 12px 16px; 
  max-width: 80%; /* Default mobile/text-only width */
}

.llm_message { 
  background: #ffffff; 
  color: #333; 
  border: 1px solid #ddd;
  border-radius: 18px 18px 18px 2px; 
  padding: 12px 16px; 
  max-width: 85%; 
}

.role-label { font-size: 10px; font-weight: bold; text-transform: uppercase; display: block; margin-bottom: 4px; opacity: 0.8; }
.message-text { white-space: pre-wrap; word-wrap: break-word; }

/* Image Elements */
.preview-container {
  position: relative; display: inline-block; margin-bottom: 12px;
  img { width: 60px; height: 60px; object-fit: cover; border-radius: 8px; }
  .remove-preview { 
    position: absolute; top: -8px; right: -8px; background: #ff4d4d; color: white; 
    border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; 
    justify-content: center; cursor: pointer; font-size: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
}

.instruction-section {
  background: #4F6675; color: white; padding: 1rem; text-align: center;
  .instruction-section-title { font-size: 1.25rem; margin-bottom: 0; font-weight: 600; }
  .instruction-section-text { font-size: 0.85rem; margin-bottom: 0; opacity: 0.9; }
}

#chatInput { border-radius: 20px; resize: none; border: 1px solid #ced4da; }
.action-btn { width: 42px; height: 42px; display: flex; align-items: center; justify-content: center; }
.cursor-pointer { cursor: pointer; }
</style>